import React, { useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText as MuiListItemText,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  Avatar,
} from "@mui/material";
import {
  DoneAll,
  MarkEmailRead,
  MarkEmailUnread,
  Refresh,
} from "@mui/icons-material";
import { supabase } from "../lib/supabaseClient";
import DashboardLayout from "../components/DashboardLayout";

function timeAgo(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
}

const NotificationsPageContent = ({ user }) => {
  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const handleSnackClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((s) => ({ ...s, open: false }));
  };

  const fetchAll = async (uid) => {
    if (!uid) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      setSnack({
        open: true,
        msg: error.message?.includes("relation")
          ? "Notifications table not found. Run the SQL migration."
          : "Failed to load notifications.",
      });
      setRows([]);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  };

  // initial + realtime
  useEffect(() => {
    if (!user?.id) return;
    fetchAll(user.id);

    const ch = supabase
      .channel(`notif-realtime-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchAll(user.id)
      )
      .subscribe();

    return () => supabase.removeChannel(ch);
  }, [user?.id]);

  const unreadCount = useMemo(
    () => rows.filter((r) => !r.is_read).length,
    [rows]
  );

  // actions
  const markAllRead = async () => {
    if (!user?.id || unreadCount === 0) return;
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", user.id)
      .eq("is_read", false);
    if (error) {
      setSnack({ open: true, msg: "Failed to mark all read." });
    } else {
      setSnack({ open: true, msg: "All notifications marked read." });
      fetchAll(user.id);
    }
  };

  const toggleRead = async (id, is_read) => {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: !is_read })
      .eq("id", id);
    if (error) {
      setSnack({ open: true, msg: "Failed to update." });
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, is_read: !is_read } : r))
    );
  };

  const hardRefresh = () => user?.id && fetchAll(user.id);

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" gutterBottom sx={{ m: 0 }}>
          Notifications
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={hardRefresh} disabled={loading}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            startIcon={<DoneAll />}
            onClick={markAllRead}
            disabled={!unreadCount}
            variant="outlined"
          >
            Mark all read {unreadCount ? `(${unreadCount})` : ""}
          </Button>
        </Stack>
      </Stack>

      <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
        <Divider sx={{ mb: 2 }} />

        {!user ? (
          <Box sx={{ minHeight: 200, display: "grid", placeItems: "center" }}>
            <Typography color="text.secondary">
              Please sign in to see notifications.
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ minHeight: 200, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ color: "text.secondary" }}>No notifications yet.</Box>
        ) : (
          <List sx={{ p: 0 }}>
            {rows.map((n) => (
              <ListItem
                key={n.id}
                alignItems="flex-start"
                sx={{
                  bgcolor: (theme) =>
                    n.is_read
                      ? "background.paper"
                      : alpha(theme.palette.primary.main, 0.08),
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  mb: 1,
                }}
                secondaryAction={
                  <IconButton onClick={() => toggleRead(n.id, n.is_read)}>
                    {n.is_read ? <MarkEmailUnread /> : <MarkEmailRead />}
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>{(n.type?.[0] || "N").toUpperCase()}</Avatar>
                </ListItemAvatar>
                <MuiListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {n.title || "Notification"}
                      </Typography>
                      {!!n.type && (
                        <Chip
                          size="small"
                          label={n.type.replaceAll("_", " ")}
                          variant="outlined"
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        • {timeAgo(n.created_at)}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.primary">
                        {n.body}
                      </Typography>
                      {n.link && (
                        <Button
                          size="small"
                          sx={{ mt: 1, px: 0 }}
                          onClick={() =>
                            window.open(n.link, "_blank", "noopener,noreferrer")
                          }
                        >
                          View details →
                        </Button>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        message={snack.msg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default function NotificationsPage() {
  return <DashboardLayout children={<NotificationsPageContent />} />;
}
